<script lang="ts">
const __DsBlock = {
	name: 'PolicyDocument',
	type: 'input',
	category: 'input',
	hidden: true,
}
</script>
<script setup lang="ts">
import type { PolicyDocument } from '#layers/access/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { LazyRulesPolicyDocument } from '#layers/access/lazy';
import { AdminPolicyDocumentView, isAdminPolicyDocument, useLastAdminPolicyCheck } from '#layers/access/public';
import { defineBlock } from '#layers/block-engine/public';
import { injectFormBlockContext, useFormFieldChanges } from '#layers/forms/public';

export interface InputPolicyDocumentProps extends Pick<InputBlockProps, 'disabled' | 'disabledNotice'> {}

const props = defineProps<InputPolicyDocumentProps>();
const { t } = useI18n();

;

const EMPTY_POLICY_DOCUMENT: PolicyDocument = {
	access: 'rules',
	itemRules: {},
	fieldRules: {},
	validationRules: {},
	entitlements: [],
};

const formBlockContext = injectFormBlockContext(null);
const policyContent = computed<PolicyDocument | null>({
	get() {
		return formBlockContext?.state.value?.content ?? null;
	},
	set(value) {
		if (formBlockContext?.state.value) {
			formBlockContext.state.value.content = value;
		}
	},
});

// keep the rules-based document while admin is enabled so toggling back restores it
const stashedRulesDocument = ref<PolicyDocument | null>(null);

const isAdmin = computed({
	get() {
		return policyContent.value?.access === 'admin';
	},
	set(value) {
		if (value) {
			stashedRulesDocument.value = policyContent.value;
			policyContent.value = { access: 'admin' };
		}
		else {
			policyContent.value = stashedRulesDocument.value ?? structuredClone(EMPTY_POLICY_DOCUMENT);
			stashedRulesDocument.value = null;
		}
	},
});

const policyId = computed(() => {
	const key = formBlockContext?.itemKey?.value;
	return key && typeof key === 'object' && 'id' in key ? String(key.id) : null;
});

const { wouldLockOut } = useLastAdminPolicyCheck({
	policyId,
	isAdmin,
});

const adminToggleDisabled = computed(() => props.disabled || wouldLockOut.value);

const { hasChanges, originalValue } = useFormFieldChanges<PolicyDocument>(formBlockContext, 'content');
const originalIsAdmin = computed(() => originalValue.value?.access === 'admin');
const adminChanged = computed(() => hasChanges.value && originalIsAdmin.value !== isAdmin.value);

watch(policyContent, () => {
	// initialize policy document to a valid default
	if (policyContent.value === null) {
		policyContent.value = structuredClone(EMPTY_POLICY_DOCUMENT);
	}
}, {
	immediate: true,
});
</script>

<template>
	<DFormLayout>
		<DNotice
			v-if="wouldLockOut"
			color="warning"
			:title="t('policy.disable_admin_lockout_warning_title')"
			:description="t('policy.disable_admin_lockout_warning_description')"
			class="col-span-full"
		/>
		<DFormField
			layout="side-by-side"
			layout-split="1-1"
			:label="t('policy.enable_admin_access')"
			:description="t('policy.enable_admin_access_description')"
			:dirty="adminChanged"
			:ui="{ labelWrapper: adminChanged ? 'pe-4' : undefined }"
		>
			<DSwitch v-model="isAdmin" :disabled="adminToggleDisabled" />
		</DFormField>
		<div v-if="policyContent" class="col-span-full">
			<AdminPolicyDocumentView v-if="isAdminPolicyDocument(policyContent)" />
			<LazyRulesPolicyDocument v-else v-model="policyContent" />
		</div>
	</DFormLayout>
</template>
